import React from 'react';
import {useTranslation} from 'react-i18next';
import {loadStripe} from '@stripe/stripe-js/pure';
import {Elements} from '@stripe/react-stripe-js';
import {StripeElementLocale} from '@stripe/stripe-js';
import {getEnvVariable} from '../utils/common';
import {usePaymentSettings} from '../hooks';

type StripeProviderProps = {
  children: React.ReactNode | JSX.Element;
};

function StripeProvider({children}: StripeProviderProps) {
  const {i18n} = useTranslation();
  const {paymentSettings, isStripePaymentProvider} = usePaymentSettings();

  const stripeAccount = paymentSettings?.external_account_id;
  const stripePromise = React.useMemo(() => {
    const needLoadStripe = isStripePaymentProvider && stripeAccount;
    const stripeKey = getEnvVariable('REACT_APP_STRIPE_API_KEY');

    if (!needLoadStripe) {
      return null;
    }

    return loadStripe(stripeKey, {stripeAccount});
  }, [isStripePaymentProvider, stripeAccount]);

  const options = React.useMemo(
    () => ({locale: i18n.language as StripeElementLocale}),
    [i18n.language],
  );

  return (
    <Elements
      children={children}
      stripe={stripePromise}
      key={stripeAccount}
      options={options}
    />
  );
}

export {StripeProvider};
